import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import { ColumnDTO, Combobox, ComboboxRef, downloadFile } from 'medere-ui-framework';
import { Sector } from '../../interface/interface';
import { RepositoryItemChangeDTO, RepositoryItemDTO, RepositoryStockItemOptionDTO } from '../../dto/SectorRepositoryDTO';
import {
    exportRepositoryItems,
    getRepositoryItems,
    getRepositoryPermissions,
    saveRepositoryItems,
    searchRepositoryStockItems,
} from '../../services/SectorRepositoryController';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import LoadTable, { LoadTableColumn, LoadTableRow } from '../../components/novarium/LoadTable';
import NvTooltips from '../../components/novarium/NvTooltips';
import { FieldError } from '../../components/novarium/NvTableParts';
import LocationInlineEditActions from '../Location/components/LocationInlineEditActions';
import StockMovementsDialog from './StockMovementsDialog';
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu';
import './StockSectors.scss';

const MAX_OPTIMAL_QUANTITY = 99999;
const MAX_REAL_STOCK = 9999999;

const TABLE_SCROLL_HEIGHT = 'calc(100vh - 420px)';

const EXPORT_COLUMNS: ColumnDTO[] = [
    { field: 'fulldescription', header: 'Artículo' },
    { field: 'stockunitdescription', header: 'Unidad' },
    { field: 'optimalquantity', header: 'Cantidad óptima' },
    { field: 'currentstock', header: 'Stock actual' },
    { field: 'externalcode', header: 'Código externo' },
];

function exportFileName(sector: Sector): string {
    const clean = (value: string | null | undefined) => (value ?? '').replace(/[\\/:*?"<>|]/g, '-').trim();
    const parts = [clean(sector.name), clean(sector.buildigname)].filter(Boolean).join(', ');
    return `Repositorio${parts ? ` - ${parts}` : ''}.xlsx`;
}

const quantityFormat = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 });

type RowStatus = 'saved' | 'new' | 'modified';

type Row = LoadTableRow & {
    repositoryDetail: number | null;
    stockItemPresentation: number;
    description: string;
    stockUnit: string;
    externalCode: string | null;
    optimalQuantity: number;
    currentStock: number;
    realStock: number;
    status: RowStatus;
    savedOptimalQuantity: number;
};

type FieldName = 'item' | 'optimalQuantity' | 'realStock';
type FieldErrors = Partial<Record<FieldName, string>>;

interface Draft {
    option: RepositoryStockItemOptionDTO | null;
    optimalQuantity: number | null;
    realStock: number | null;
}

const EMPTY_DRAFT: Draft = { option: null, optimalQuantity: null, realStock: null };

const toRow = (item: RepositoryItemDTO): Row => ({
    rowKey: `rd-${item.repositorydetail}`,
    repositoryDetail: item.repositorydetail,
    stockItemPresentation: item.stockitempresentation,
    description: item.fulldescription,
    stockUnit: item.stockunitdescription,
    externalCode: item.externalcode,
    optimalQuantity: Number(item.optimalquantity ?? 0),
    currentStock: Number(item.currentstock ?? 0),
    realStock: Number(item.currentstock ?? 0),
    status: 'saved',
    savedOptimalQuantity: Number(item.optimalquantity ?? 0),
});

const normalize = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

const formatQuantity = (value: number | null | undefined) => (value == null ? '—' : quantityFormat.format(value));

interface Props {
    sector: Sector;
    onBack: () => void;
}

export default function SectorRepositoryView({ sector, onBack }: Props) {
    const notify = useNotify();

    const [rows, setRows] = useState<Row[]>([]);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [itemFilter, setItemFilter] = useState('');
    const [onlyBelowOptimal, setOnlyBelowOptimal] = useState(false);
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [historyItem, setHistoryItem] = useState<{ repositoryDetail: number; description: string } | null>(null);
    const comboRef = useRef<ComboboxRef>(null);
    const optimalRef = useRef<HTMLInputElement>(null);
    const realStockRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);
        try {
            const [items, permissions] = await Promise.all([getRepositoryItems(sector.sector), getRepositoryPermissions()]);
            setRows(items.map(toRow));
            setCanEdit(permissions.canSetupRepository);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            console.error(apiError.errorCode, apiError.path);
            setLoadFailed(true);
            notify.error(apiError.status >= 400 && apiError.status < 500 || apiError.status === 502
                ? apiError.message
                : 'No se pudo cargar el repositorio. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [sector.sector, notify]);

    useEffect(() => { load(); }, [load]);

    const pendingRows = useMemo(() => rows.filter(r => r.status !== 'saved'), [rows]);
    const hasPendingChanges = pendingRows.length > 0;

    const visibleRows = useMemo(() => {
        const filter = normalize(itemFilter);
        return rows.filter(r =>
            (!filter || normalize(r.description).includes(filter)) &&
            (!onlyBelowOptimal || r.currentStock < r.optimalQuantity));
    }, [rows, itemFilter, onlyBelowOptimal]);

    function resetDraft() {
        setDraft(EMPTY_DRAFT);
        setEditingKey(null);
        setErrors({});
    }

    function validateDraft(): FieldErrors {
        const result: FieldErrors = {};
        if (!draft.option) result.item = 'Elegí el artículo';
        else if (!editingKey && rows.some(r => r.stockItemPresentation === draft.option!.id)) result.item = 'Ese artículo ya está en el repositorio';
        if (draft.optimalQuantity == null) result.optimalQuantity = 'Ingresá la cantidad óptima';
        else if (draft.optimalQuantity < 0 || draft.optimalQuantity > MAX_OPTIMAL_QUANTITY) result.optimalQuantity = `Ingresá de 0 a ${quantityFormat.format(MAX_OPTIMAL_QUANTITY)}`;
        if (draft.realStock == null) result.realStock = 'Ingresá el stock real';
        else if (draft.realStock < 0 || draft.realStock > MAX_REAL_STOCK) result.realStock = `Ingresá de 0 a ${quantityFormat.format(MAX_REAL_STOCK)}`;
        return result;
    }

    function focusFirstError(fieldErrors: FieldErrors) {
        if (fieldErrors.item) comboRef.current?.focus();
        else if (fieldErrors.optimalQuantity) optimalRef.current?.focus();
        else if (fieldErrors.realStock) realStockRef.current?.focus();
    }

    function handleSubmitDraft() {
        const validationErrors = validateDraft();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            focusFirstError(validationErrors);
            return;
        }
        const optimalQuantity = draft.optimalQuantity!;
        const realStock = draft.realStock!;
        if (editingKey) {
            setRows(current => current.map(r => {
                if (r.rowKey !== editingKey) return r;
                const unchanged = optimalQuantity === r.savedOptimalQuantity && realStock === r.currentStock;
                const status: RowStatus = r.status === 'new' ? 'new' : unchanged ? 'saved' : 'modified';
                return { ...r, optimalQuantity, realStock, status };
            }));
        } else {
            const option = draft.option!;
            const row: Row = {
                rowKey: `new-${option.id}`,
                repositoryDetail: null,
                stockItemPresentation: option.id,
                description: option.fullDescription,
                stockUnit: option.stockUnitDescription,
                externalCode: option.externalCode,
                optimalQuantity,
                currentStock: 0,
                realStock,
                status: 'new',
                savedOptimalQuantity: optimalQuantity,
            };
            setRows(current => [row, ...current]);
        }
        resetDraft();
        comboRef.current?.focus();
    }

    function startEdit(row: Row) {
        setDraft({
            option: { id: row.stockItemPresentation, fullDescription: row.description, stockUnitDescription: row.stockUnit, externalCode: row.externalCode },
            optimalQuantity: row.optimalQuantity,
            realStock: row.realStock,
        });
        setEditingKey(row.rowKey);
        setErrors({});
        optimalRef.current?.focus();
    }

    function removeNewRow(row: Row) {
        setRows(current => current.filter(r => r.rowKey !== row.rowKey));
        if (editingKey === row.rowKey) resetDraft();
    }

    function handleDraftKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmitDraft();
        } else if (event.key === 'Escape' && editingKey) {
            event.preventDefault();
            resetDraft();
        }
    }

    async function save(): Promise<boolean> {
        if (saving) return false;
        if (!hasPendingChanges) {
            notify.success('No hay cambios para guardar');
            return true;
        }
        const changes: RepositoryItemChangeDTO[] = pendingRows.map(r => ({
            repositoryDetail: r.repositoryDetail,
            stockItemPresentation: r.stockItemPresentation,
            optimalQuantity: r.optimalQuantity,
            realStock: r.status === 'new' || r.realStock !== r.currentStock ? r.realStock : null,
        }));
        setSaving(true);
        try {
            const items = await saveRepositoryItems(sector.sector, changes);
            setRows(items.map(toRow));
            resetDraft();
            notify.success('Cambios guardados');
            return true;
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            console.error(apiError.errorCode, apiError.path);
            const isBusinessError = apiError.status >= 400 && apiError.status < 500 || apiError.status === 502;
            notify.error(isBusinessError ? apiError.message : 'No se pudieron guardar los cambios. Intentá de nuevo.');
            return false;
        } finally {
            setSaving(false);
        }
    }

    function leave() {
        if (!hasPendingChanges) {
            onBack();
            return;
        }
        confirmDialog({
            header: '¿Salir sin guardar?',
            message: 'Los cambios que hiciste se van a perder.',
            className: 'nv-scope nv-confirm',
            footer: (options) => (
                <>
                    <Button type="button" link label="Seguir editando" className="nv-push-left" onClick={options.reject} autoFocus />
                    <Button type="button" className="buttonMain2" outlined label="Salir sin guardar" onClick={options.accept} />
                    <Button type="button" className="buttonMain" label="Guardar y salir" onClick={async () => { options.reject(); if (await save()) onBack(); }} />
                </>
            ),
            accept: onBack,
        });
    }

    async function handleExport() {
        setExporting(true);
        try {
            await downloadFile(await exportRepositoryItems(sector.sector, { itemFilter, onlyBelowOptimal }, EXPORT_COLUMNS),
                exportFileName(sector));
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            console.error(apiError.errorCode, apiError.path);
            notify.error('No se pudo exportar el repositorio. Intentá de nuevo.');
        } finally {
            setExporting(false);
        }
    }

    // ── Tabla ──

    const editingRow = editingKey ? rows.find(r => r.rowKey === editingKey) : undefined;

    const columns: LoadTableColumn<Row>[] = [
        {
            key: 'item',
            header: 'Artículo',
            className: 'nv-col-item nv-col-item-wide',
            addCell: (
                <div className={classNames('nv-cell-field', { 'nv-field-locked': editingKey != null })} onKeyDown={handleDraftKeyDown}>
                    <Combobox
                        ref={comboRef}
                        getItems={searchRepositoryStockItems}
                        value={draft.option}
                        setValue={(option: RepositoryStockItemOptionDTO | null) => {
                            const existing = option && !editingKey ? rows.find(r => r.stockItemPresentation === option.id) : undefined;
                            if (existing) {
                                startEdit(existing);
                                return;
                            }
                            setDraft(current => ({ ...current, option: option ?? null }));
                            setErrors(current => ({ ...current, item: undefined }));
                        }}
                        optionLabel="fullDescription"
                        placeholder="Buscá por nombre"
                        minPanelWidth={500}
                        maxPanelWidth={800}
                        disable={editingKey != null}
                        error={Boolean(errors.item)}
                        caption={errors.item}
                    />
                </div>
            ),
            body: (row) => (
                <span className="nv-row-8">
                    <span className="nv-truncate nv-tip" data-pr-tooltip={row.description}>{row.description}</span>
                    {row.status !== 'saved' && <Tag className="nv-tag-warn" value="Sin guardar" />}
                </span>
            ),
        },
        {
            key: 'unit',
            header: 'Unidad',
            className: 'nv-col-code',
            addCell: <span className="nv-muted">{draft.option?.stockUnitDescription || '—'}</span>,
            body: (row) => row.stockUnit || '—',
        },
        {
            key: 'optimal',
            header: 'Cantidad óptima',
            align: 'right',
            className: 'nv-col-qty',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleDraftKeyDown}>
                    <InputNumber
                        inputRef={optimalRef}
                        value={draft.optimalQuantity}
                        onValueChange={(e) => {
                            setDraft(current => ({ ...current, optimalQuantity: e.value ?? null }));
                            setErrors(current => ({ ...current, optimalQuantity: undefined }));
                        }}
                        mode="decimal"
                        locale="es-AR"
                        maxFractionDigits={0}
                        min={0}
                        max={MAX_OPTIMAL_QUANTITY}
                        className={classNames('nv-number nv-in-cell', { 'p-invalid': errors.optimalQuantity })}
                        inputClassName={classNames({ 'p-filled': draft.optimalQuantity != null })}
                        aria-label="Cantidad óptima"
                        aria-invalid={errors.optimalQuantity ? true : undefined}
                        aria-describedby={errors.optimalQuantity ? 'repository-optimal-error' : undefined}
                    />
                    <FieldError id="repository-optimal-error" message={errors.optimalQuantity} />
                </div>
            ),
            body: (row) => formatQuantity(row.optimalQuantity),
        },
        {
            key: 'current',
            header: 'Stock actual',
            align: 'right',
            className: 'nv-col-qty',
            addCell: <span className="nv-muted">{editingRow ? formatQuantity(editingRow.currentStock) : '—'}</span>,
            body: (row) => (row.status === 'new' ? '—' : formatQuantity(row.currentStock)),
        },
        {
            key: 'real',
            header: 'Stock real',
            align: 'right',
            className: 'nv-col-qty',
            addCell: (
                <div className="nv-cell-field" onKeyDown={handleDraftKeyDown}>
                    <InputNumber
                        inputRef={realStockRef}
                        value={draft.realStock}
                        onValueChange={(e) => {
                            setDraft(current => ({ ...current, realStock: e.value ?? null }));
                            setErrors(current => ({ ...current, realStock: undefined }));
                        }}
                        mode="decimal"
                        locale="es-AR"
                        maxFractionDigits={0}
                        min={0}
                        max={MAX_REAL_STOCK}
                        className={classNames('nv-number nv-in-cell', { 'p-invalid': errors.realStock })}
                        inputClassName={classNames({ 'p-filled': draft.realStock != null })}
                        aria-label="Stock real"
                        aria-invalid={errors.realStock ? true : undefined}
                        aria-describedby={errors.realStock ? 'repository-real-error' : undefined}
                    />
                    <FieldError id="repository-real-error" message={errors.realStock} />
                </div>
            ),
            body: (row) => formatQuantity(row.realStock),
        },
        {
            key: 'externalCode',
            header: 'Código externo',
            className: 'nv-col-code',
            addCell: <span className="nv-muted">{draft.option?.externalCode || '—'}</span>,
            body: (row) => row.externalCode || '—',
        },
    ];

    const addAction = editingKey ? (
        <LocationInlineEditActions visible onConfirm={handleSubmitDraft} onCancel={resetDraft} />
    ) : (
        <Button
            type="button"
            icon="pi pi-plus-circle text-3xl"
            className="buttonSecondary2 p-button-rounded nv-add-row-btn nv-tip"
            aria-label="Agregar"
            data-pr-tooltip="Agregar"
            onClick={handleSubmitDraft}
        />
    );

    const rowActions = (row: Row) => {
        const items: LocationActionItem[] = [
            { label: 'Editar', icon: 'pi pi-file-edit', action: () => startEdit(row), disabled: !canEdit || saving },
            row.repositoryDetail != null
                ? { label: 'Historial de movimientos', icon: 'pi pi-history', action: () => setHistoryItem({ repositoryDetail: row.repositoryDetail!, description: row.description }) }
                : { label: 'Quitar', icon: 'pi pi-trash', action: () => removeNewRow(row), disabled: saving },
        ];
        return (
            <LocationActionMenu
                items={items}
                ariaLabel={`Opciones de ${row.description}`}
                buttonId={`repositoryItemMenu_${row.rowKey}`}
                panelClassName="stock-sectors-action-menu-panel"
                buttonClassName="p-button-text nv-icon"
            />
        );
    };

    const filtered = itemFilter.trim() !== '' || onlyBelowOptimal;
    const emptyMessage = filtered ? 'No hay artículos para estos filtros.' : 'Sin artículos cargados';

    return (
        <div className="nv-scope nv-page stock-sectors-view">
            <NvTooltips />
            <ConfirmDialog />
            <div className="nv-page-head">
                <Button type="button" link icon="pi pi-angle-double-left" label="Volver" onClick={leave} />
                <h1 className="nv-h1">Configurar repositorio</h1>
                <p className="nv-subtitle">{sector.name} · {sector.buildigname}</p>
                {!canEdit && !loading && !loadFailed && (
                    <p className="nv-note">
                        <i className="pi pi-info-circle" aria-hidden="true" />
                        Tu perfil puede ver el repositorio, pero no agregar artículos ni cambiar cantidades.
                    </p>
                )}
            </div>

            {loading ? (
                <div className="nv-loading" role="status">
                    <ProgressSpinner strokeWidth="4" style={{ width: 32, height: 32 }} />
                    <span>Cargando…</span>
                </div>
            ) : loadFailed ? (
                <div className="nv-row-8">
                    <p className="nv-muted">No se pudo cargar el repositorio.</p>
                    <Button type="button" className="buttonMain3" label="Volver a cargar" onClick={load} />
                </div>
            ) : (
                <>
                    <div className="nv-head-row">
                        <div className="nv-toolbar">
                            <span className={classNames('nv-search nv-w-l', { 'nv-filter-applied': itemFilter.trim() !== '' })}>
                                <i className="pi pi-search" aria-hidden="true" />
                                <InputText
                                    value={itemFilter}
                                    onChange={(e) => setItemFilter(e.target.value)}
                                    placeholder="Buscar artículo"
                                    aria-label="Buscar artículo"
                                />
                            </span>
                            <div className="nv-check-row">
                                <InputSwitch inputId="repository-below-optimal" checked={onlyBelowOptimal} onChange={(e) => setOnlyBelowOptimal(!!e.value)} />
                                <label htmlFor="repository-below-optimal">Solo debajo de la cantidad óptima</label>
                            </div>
                        </div>
                        <span
                            className={classNames({ 'nv-disabled-tip nv-tip': hasPendingChanges })}
                            data-pr-tooltip={hasPendingChanges ? 'Guardá los cambios para exportarlos' : undefined}
                            tabIndex={hasPendingChanges ? 0 : undefined}
                        >
                            <Button type="button" className="buttonMain2" outlined icon="pi pi-file-excel" label="Exportar"
                                loading={exporting} disabled={hasPendingChanges} onClick={handleExport} />
                        </span>
                    </div>

                    <div className="nv-actions-centered">
                        <LoadTable
                            rows={visibleRows}
                            columns={columns}
                            addAction={addAction}
                            rowActions={rowActions}
                            emptyMessage={emptyMessage}
                            editingRowKey={editingKey}
                            showAddRow={canEdit}
                            virtualScroll
                            scrollHeight={TABLE_SCROLL_HEIGHT}
                        />
                    </div>

                    {canEdit && (
                        <div className="nv-page-foot">
                            <div className="nv-divider" />
                            <div className="nv-actions-end">
                                <Button type="button" className="buttonMain2" outlined label="Cancelar" disabled={saving} onClick={leave} />
                                <Button type="button" className="buttonMain" label="Guardar cambios" loading={saving} onClick={() => { void save(); }} />
                            </div>
                        </div>
                    )}
                </>
            )}

            <StockMovementsDialog item={historyItem} sectorName={`${sector.name} · ${sector.buildigname}`} onHide={() => setHistoryItem(null)} />
        </div>
    );
}
